import React, { useCallback, useEffect, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight, Images, ZoomIn, X } from "lucide-react";
import { getServicePreviewText, stripHtml } from "../lib/html";
import HtmlContent from "./HtmlContent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "./ui/dialog";
import { type, typeRichDescription } from "../lib/typography";

function GalleryImagesLightbox({ images, albumTitle, activeIndex, onClose, onNavigate }) {
  const image = images[activeIndex];
  const canNavigate = images.length > 1;

  const goPrev = useCallback(() => {
    if (!canNavigate) return;
    onNavigate((activeIndex - 1 + images.length) % images.length);
  }, [activeIndex, canNavigate, images.length, onNavigate]);

  const goNext = useCallback(() => {
    if (!canNavigate) return;
    onNavigate((activeIndex + 1) % images.length);
  }, [activeIndex, canNavigate, images.length, onNavigate]);

  useEffect(() => {
    if (!image) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [image, goPrev, goNext, onClose]);

  if (!image) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className="max-w-5xl max-h-[92vh] overflow-hidden p-0 gap-0 border-0 bg-[#0a1a2f] text-white">
        <DialogTitle className="sr-only">{albumTitle} — image {activeIndex + 1}</DialogTitle>
        <DialogDescription className="sr-only">
          Gallery photo {activeIndex + 1} of {images.length}
        </DialogDescription>

        <div className="relative bg-black">
          <img
            src={image}
            alt={`${albumTitle} — ${activeIndex + 1}`}
            className="w-full max-h-[75vh] object-contain mx-auto"
          />

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 z-20 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 border border-white/25 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {canNavigate && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        <div className="px-6 py-4 flex items-center justify-between gap-4 border-t border-white/10">
          <p className="text-sm font-semibold text-slate-200 line-clamp-1">{albumTitle}</p>
          <span className="text-xs font-bold text-slate-400 shrink-0">
            {activeIndex + 1} / {images.length}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GalleryImageCarousel({
  images,
  alt,
  activeImageIndex,
  onIndexChange,
  onOpenLightbox,
  pauseAutoSlide,
  onImageError,
}) {
  const activeImage = images[activeImageIndex] || null;
  const showImage = Boolean(activeImage?.trim());
  const canSlide = images.length > 1;

  useEffect(() => {
    onIndexChange(0);
  }, [images.length, alt, onIndexChange]);

  const goPrev = (e) => {
    e.stopPropagation();
    onIndexChange((prev) => (prev - 1 + images.length) % images.length);
  };

  const goNext = (e) => {
    e.stopPropagation();
    onIndexChange((prev) => (prev + 1) % images.length);
  };

  useEffect(() => {
    if (!canSlide || pauseAutoSlide) return undefined;
    const timer = setInterval(() => {
      onIndexChange((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [canSlide, pauseAutoSlide, images.length, onIndexChange]);

  const handleOpenLightbox = () => {
    if (images.length > 0) onOpenLightbox(activeImageIndex);
  };

  return (
    <div className="relative aspect-[4/3] overflow-hidden bg-[#0a1a2f]">
      {showImage ? (
        <img
          src={activeImage}
          alt={alt}
          onClick={handleOpenLightbox}
          onError={() => onImageError?.(activeImage)}
          className="absolute inset-0 h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-700 ease-out cursor-zoom-in"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1a2f] via-[#0f2540] to-[#1e6fcf]/30 flex items-center justify-center">
          <Images className="w-14 h-14 text-white/20" />
        </div>
      )}

      {images.length > 0 && (
        <button
          type="button"
          onClick={handleOpenLightbox}
          className="absolute inset-0 z-10 flex items-center justify-center bg-[#0a1a2f]/0 group-hover:bg-[#0a1a2f]/30 transition-colors opacity-0 group-hover:opacity-100 cursor-zoom-in"
          aria-label={
            images.length > 1 ? `View all ${images.length} gallery images` : "View gallery image"
          }
        >
          <span className="inline-flex items-center gap-2 bg-white/95 text-[#0a1a2f] font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-full shadow-lg pointer-events-none">
            <ZoomIn className="w-4 h-4" />
            {images.length > 1 ? `View all ${images.length} images` : "View image"}
          </span>
        </button>
      )}

      {canSlide && (
        <>
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/35 hover:bg-[#1e6fcf] text-white flex items-center justify-center transition-colors"
            aria-label="Previous gallery image"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/35 hover:bg-[#1e6fcf] text-white flex items-center justify-center transition-colors"
            aria-label="Next gallery image"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute top-3 right-3 z-20 px-2.5 py-1 rounded-full bg-black/45 text-white text-[10px] font-bold tracking-wider pointer-events-none">
            {activeImageIndex + 1}/{images.length}
          </div>
        </>
      )}
    </div>
  );
}

function formatGalleryDate(date) {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

export default function GalleryCard({ album, compact = false }) {
  const images = Array.isArray(album.imageUrls) ? album.imageUrls : [];
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [failedImages, setFailedImages] = useState([]);

  useEffect(() => {
    setActiveImageIndex(0);
    setFailedImages([]);
  }, [album.id, images.length]);

  const visibleImages = images.filter((img) => !failedImages.includes(img));

  const handleImageError = (url) => {
    if (!url) return;
    setFailedImages((prev) => (prev.includes(url) ? prev : [...prev, url]));
    setActiveImageIndex(0);
  };

  const openLightbox = (index) => {
    if (visibleImages.length === 0) return;
    setLightboxIndex(index);
  };

  const descriptionPreview =
    getServicePreviewText(album.description, album.title) ||
    stripHtml(album.description);

  return (
    <article className="group flex flex-col h-full rounded-xl border border-slate-200 bg-white overflow-hidden hover:border-[#1e6fcf] hover:shadow-xl transition-all duration-300">
      <GalleryImageCarousel
        images={visibleImages}
        alt={album.title}
        activeImageIndex={activeImageIndex}
        onIndexChange={setActiveImageIndex}
        onOpenLightbox={openLightbox}
        pauseAutoSlide={lightboxIndex !== null}
        onImageError={handleImageError}
      />

      <div className="flex flex-col flex-1 p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          {album.category ? (
            <span className={`${type.eyebrow} text-[#1758a8]`}>
              {album.category}
            </span>
          ) : (
            <span />
          )}
          {album.date && (
            <span className={`inline-flex items-center gap-1.5 ${type.meta} shrink-0`}>
              <Calendar className="w-3.5 h-3.5 text-[#1e6fcf]" />
              {formatGalleryDate(album.date)}
            </span>
          )}
        </div>

        <h3 className={`${type.cardTitle} text-[#0a1a2f] group-hover:text-[#1758a8] transition-colors`}>
          {album.title}
        </h3>

        {visibleImages.length > 0 && (
          <button
            type="button"
            onClick={() => openLightbox(activeImageIndex)}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#1758a8] hover:text-[#1e6fcf] transition-colors w-fit"
          >
            <Images className="w-3.5 h-3.5" />
            {visibleImages.length > 1
              ? `View all ${visibleImages.length} images`
              : "View full image"}
          </button>
        )}

        {album.description && (
          compact ? (
            descriptionPreview && (
              <p className={`mt-3 ${typeRichDescription} line-clamp-3`}>
                {descriptionPreview}
              </p>
            )
          ) : (
            <div className="mt-3 max-h-52 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
              <HtmlContent
                html={album.description}
                className={typeRichDescription}
              />
            </div>
          )
        )}
      </div>

      {lightboxIndex !== null && visibleImages.length > 0 && (
        <GalleryImagesLightbox
          images={visibleImages}
          albumTitle={album.title}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </article>
  );
}
